import type { JSX } from "react";

import dayjs from "dayjs";
import styled from "styled-components";

import { shortMonthShortDayAndYear, serverResponseFormat } from "@similarweb/pro-logic-components";
import Sentry from "@similarweb/sentry-logger";
import { ListItemWebsite } from "@similarweb/ui-components";

import type { StateObjectType } from "app/@types/userdata";
import { swSettings } from "app/common/services/swSettings";
import type { ICampaignsScrapingConfiguration } from "app/components/campaigns/shared/CampaignsConst";
import { dynamicFilterFilter } from "app/filters/dynamicFilter";
import { translate } from "app/filters/i18nFilter";
import { EGranularities } from "app/pages/app-intelligence/constants";
import { fetchService } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsCampaignUtils";
import {
    getRTDurationObjectForCampaign,
    withCampaignDatesUtc,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsContextUtils";
import type {
    IKeywordsCampaignData,
    IKeywordsCampaignsCampaignRawData,
    IKeywordsCampaignsUpdateCampaignData,
    IRTCurrentCampaignData,
    IRTRemoveKeywordsOrUpdateTagsOnly,
    IRTUpdateCampaignRemovedKeywords,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import {
    CLICKS_CHANGE_DECIMAL_ROUND,
    CLICKS_METRIC_DECIMAL_ROUND,
    DEFAULT_CLICKS_SORTING,
    DEFAULT_POSITION_SORTING,
    POSITIONS_DAILY_DECIMAL_ROUND,
    POSITIONS_WEEKLY_MONTHLY_DECIMAL_ROUND,
    UNDEFINED_SIGN,
    UPDATE_KEYWORDS_SUFFIX,
    UPDATE_SETTINGS_SUFFIX,
    FIRST_DAY_COLUMN_PREFIX,
    LAST_DAY_COLUMN_PREFIX,
    CHANGE_DAY_COLUMN_PREFIX,
} from "app/common/campaigns/KeywordsCampaginKeywordsTableConsts";
import type {
    IKeywordsReportRecordManagedData,
    IKeywordsReportRecordsRawData,
    IKeywordsReportTableData,
    IKeywordsReportTableDataWithKeys,
    IKeywordsReportTableMetrics,
    ISelectedSites,
    ITransferDataToRowProps,
} from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportTypes";
import { EKeywordsReportKeywordsTableMetrics } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsReportTypes";
import {
    DAY_WITHOUT_DATA,
    MAX_RANK_POSITION,
    NOT_RANK_POSITION,
    NOT_RANKED_SIGN,
} from "app/pages/digital-marketing/keywords-campaigns/reports/shared/RTSharedConsts";
import type { IRankTrackerChangeProps } from "app/pages/digital-marketing/keywords-campaigns/reports/shared/Table/expandedTable/RankTrackerExpandedTableTypes";
import {
    getLocationCellDetails,
    getSearchEngineByScrapingConfigId,
} from "app/pages/digital-marketing/keywords-campaigns/reports/shared/Table/TableUtils/RankTrackerTableSharedUtils";
import { RankTrackerQueryType } from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import type { DatesRange } from "app/pages/digital-marketing/keywords-campaigns/shared/types";
import { createRankTrackerQueryKey } from "app/pages/digital-marketing/keywords-campaigns/shared/utils";
import { getSingleCampaignURL } from "app/pages/digital-marketing/keywords-campaigns/wizard/constants";
import dateTimeService from "app/services/date-time/dateTimeService";
import queryClient from "single-spa/react-app/query-client";

import {
    normalizeCampaignSettingsPayload,
    normalizeKeywordUpdatePayload,
} from "../../../infra/KeywordsCampaignsPayloadUtils";
import { transformDisplayedURL } from "../shared/KeywordsReportUtils";

export const getTableMetrics = (
    granularity = EGranularities.DAILY,
    isSingleMode?: boolean,
    universalPositionMetric = false,
): IKeywordsReportTableMetrics[] =>
    [
        {
            metricID: EKeywordsReportKeywordsTableMetrics.POSITION,
            switcherTranslationKey: "seo.keywordsCampaign.keywordsReport.keywordsTable.tab.position.title",
            defaultSort: DEFAULT_POSITION_SORTING,
            tooltipKey: "seo.keywordsCampaign.keywordsReport.keywordsTable.tab.position.tooltip",
            metricFilter: `number:${
                granularity === EGranularities.DAILY
                    ? POSITIONS_DAILY_DECIMAL_ROUND
                    : POSITIONS_WEEKLY_MONTHLY_DECIMAL_ROUND
            }`,
            isMetricPercentage: false,
            isChangePercentage: false,
            maxChangeValidValue: MAX_RANK_POSITION,
            firstLastDateTooltipKey:
                "seo.keywordsCampaign.keywordsReport.keywordsTable.tab.position.firstLastCell.tooltip",
            columnPickerKey: "seo.keywordsCampaign.wizard.addingKeywordsStep.table.column.position.history",
        },
        universalPositionMetric
            ? {
                  metricID: EKeywordsReportKeywordsTableMetrics.UNIVERSAL_POSITION,
                  switcherTranslationKey: translate(
                      "rankTracker.keywordsCampaign.keywordsReport.keywordsTable.overallPosition.title",
                  ),
                  defaultSort: DEFAULT_POSITION_SORTING,
                  tooltipKey: "rankTracker.keywordsCampaign.keywordsReport.keywordsTable.overallPosition.tooltip",
                  metricFilter: `number:${
                      granularity === EGranularities.DAILY
                          ? POSITIONS_DAILY_DECIMAL_ROUND
                          : POSITIONS_WEEKLY_MONTHLY_DECIMAL_ROUND
                  }`,
                  isMetricPercentage: false,
                  isChangePercentage: false,
                  firstLastDateTooltipKey:
                      "rankTracker.keywordsCampaign.keywordsReport.keywordsTable.overallPosition.firstCell.tooltip",
                  columnPickerKey: "rankTracker.keywordsCampaign.keywordsReport.keywordsTable.overallPosition.history",
              }
            : null,
        {
            metricID: EKeywordsReportKeywordsTableMetrics.CLICKS,
            switcherTranslationKey: "seo.keywordsCampaign.keywordsReport.keywordsTable.tab.clicks.title",
            defaultSort: DEFAULT_CLICKS_SORTING,
            tooltipKey: "seo.keywordsCampaign.keywordsReport.keywordsTable.tab.clicks.tooltip",
            metricFilter: `abbrNumber:${CLICKS_METRIC_DECIMAL_ROUND}`,
            changeFilter: `simplePercentage:${CLICKS_CHANGE_DECIMAL_ROUND}`,
            isMetricPercentage: false,
            isChangePercentage: true,
            firstLastDateTooltipKey:
                "seo.keywordsCampaign.keywordsReport.keywordsTable.tab.clicks.firstLastCell.tooltip",
            columnPickerKey: "seo.keywordsCampaign.wizard.addingKeywordsStep.table.column.clicks.history",
        },
        isSingleMode
            ? {
                  metricID: EKeywordsReportKeywordsTableMetrics.ALL,
                  switcherTranslationKey: "RT.keywordsReport.keywordsTable.tab.all.title",
                  defaultSort: DEFAULT_CLICKS_SORTING,
                  sortSuffix: EKeywordsReportKeywordsTableMetrics.POSITION.at(0),
                  tooltipKey: "RT.keywordsReport.keywordsTable.tab.all.tooltip",
                  metricFilter: `abbrNumber:${CLICKS_METRIC_DECIMAL_ROUND}`,
                  changeFilter: `simplePercentage:${CLICKS_CHANGE_DECIMAL_ROUND}`,
                  isMetricPercentage: false,
                  isChangePercentage: true,
                  firstLastDateTooltipKey: "RT.keywordsReport.keywordsTable.tab.all.firstLastCell.tooltip",
                  columnPickerKey: "RT.wizard.addingKeywordsStep.table.column.all.history",
              }
            : null,
    ].filter(Boolean);

const AvailableCompetitorsWrapper = styled.div`
    width: 100%;
`;

export const getAutocompleteResults = (
    query: string,
    availableSites: ISelectedSites[],
    onSiteSelected: (site: string) => void,
): JSX.Element => {
    const filteredSitesByQuery: ISelectedSites[] =
        query === "" ? availableSites : availableSites.filter((competitor) => competitor.id.indexOf(query) > -1);

    return (
        <AvailableCompetitorsWrapper key={"AvailableCompetitorsWrapper"}>
            {filteredSitesByQuery.map((site) => (
                <ListItemWebsite
                    img={site.data.mainItem}
                    key={`list-item-${site.id}`}
                    text={site.id}
                    onClick={() => onSiteSelected(site.id)}
                />
            ))}
        </AvailableCompetitorsWrapper>
    );
};

const getFinalPositionToPresent = (position: string, isPercentage: boolean): string => {
    if (position === DAY_WITHOUT_DATA.toString()) {
        return "-";
    }
    if (isPercentage && position !== UNDEFINED_SIGN) {
        return `${position}%`;
    }
    return position.toString();
};

const getChangePosition = (rank: IRankTrackerChangeProps): IRankTrackerChangeProps => {
    const returnValue = rank;
    if (rank?.first === DAY_WITHOUT_DATA) {
        returnValue.first = null;
        returnValue.change = null;
    }
    if (rank?.last === DAY_WITHOUT_DATA) {
        returnValue.last = null;
        returnValue.change = null;
    }
    return returnValue;
};

const getPositionForSingleDay = (
    rank: number,
    isMetricPercentage: boolean,
    metricFilter?: string,
    isPosition = true,
): string => {
    if (isPosition && rank === DAY_WITHOUT_DATA) {
        return UNDEFINED_SIGN;
    }
    if (isPosition && rank >= NOT_RANK_POSITION) {
        return NOT_RANKED_SIGN;
    }
    const filteredDailyRank = dynamicFilterFilter()(rank, metricFilter, UNDEFINED_SIGN);
    return getFinalPositionToPresent(filteredDailyRank, isMetricPercentage);
};

const getPositionsForSingleSite = (
    tableMetricDefinitions: IKeywordsReportTableMetrics,
    site: ISelectedSites,
    keyword: IKeywordsReportRecordsRawData,
    isPosition = true,
) => {
    const rankBySite = getChangePosition(keyword[tableMetricDefinitions.metricID][site.id]);
    const firstDayToPresent = getPositionForSingleDay(
        rankBySite?.first,
        tableMetricDefinitions.isMetricPercentage,
        tableMetricDefinitions.metricFilter,
        isPosition,
    );
    const lastDayToPresent = getPositionForSingleDay(
        rankBySite?.last,
        tableMetricDefinitions.isMetricPercentage,
        tableMetricDefinitions.metricFilter,
        isPosition,
    );
    return {
        [`${FIRST_DAY_COLUMN_PREFIX}${tableMetricDefinitions.metricID}${site.id}`]: firstDayToPresent,
        [`${LAST_DAY_COLUMN_PREFIX}${tableMetricDefinitions.metricID}${site.id}`]: lastDayToPresent,
        [`${CHANGE_DAY_COLUMN_PREFIX}${tableMetricDefinitions.metricID}${site.id}`]: rankBySite,
    };
};

const getPositionsForKeyword = (
    keyword: IKeywordsReportRecordsRawData,
    tableTab: string,
    tableGranularity: EGranularities,
    selectedSites: ISelectedSites[],
) => {
    try {
        const isSingleMode = selectedSites.length === 1;
        //Position tab Daily granularity should not present any decimal point
        const tableMetrics = getTableMetrics(tableGranularity, isSingleMode, true);
        const tableMetricDefinitions = tableMetrics.find((metric) => metric.metricID === tableTab);
        if (!tableMetricDefinitions) {
            return {};
        }
        return selectedSites.reduce((res, site) => {
            if (tableMetricDefinitions.metricID === EKeywordsReportKeywordsTableMetrics.ALL) {
                const organicPositionsDefinitions = tableMetrics.find(
                    (metric) => metric.metricID === EKeywordsReportKeywordsTableMetrics.POSITION,
                );
                // Universal position start
                const universalPositionDefinitions = tableMetrics.find(
                    (metric) => metric.metricID === EKeywordsReportKeywordsTableMetrics.UNIVERSAL_POSITION,
                );
                // Universal position end

                const clicksDefinitions = tableMetrics.find(
                    (metric) => metric.metricID === EKeywordsReportKeywordsTableMetrics.CLICKS,
                );

                return {
                    ...res,
                    ...getPositionsForSingleSite(organicPositionsDefinitions, site, keyword),
                    ...getPositionsForSingleSite(universalPositionDefinitions, site, keyword),
                    ...getPositionsForSingleSite(clicksDefinitions, site, keyword, false),
                };
            } else {
                const rankBySite = getPositionsForSingleSite(tableMetricDefinitions, site, keyword);
                return {
                    ...res,
                    ...rankBySite,
                };
            }
        }, {});
    } catch (error) {
        Sentry.errorTracker({
            message: "KeywordsReport: Error parsing table data",
            e: error,
            level: "error",
        });
        return {};
    }
};

const getFormattedLatUpdatedTime = (time: string): string => {
    const passedTimeFromUpdatedUntilNow = dateTimeService.getDateDiffFromNowLocal(time, "day");
    if (passedTimeFromUpdatedUntilNow <= 1) {
        const passedHours = dateTimeService.getDateDiffFromNowLocal(time, "hour");
        return translate("seo.keywordsCampaign.keywordsReport.keywordsTable.updated.hoursAgo", {
            amount: passedHours,
        });
    } else if (passedTimeFromUpdatedUntilNow === 7) {
        return translate("seo.keywordsCampaign.keywordsReport.keywordsTable.updated.weekAgo");
    } else {
        return dayjs(time).format(shortMonthShortDayAndYear);
    }
};

export const getCountryFromScrapingConfigId = (
    scrapingConfigurations: ICampaignsScrapingConfiguration[],
    recordScrapingConfiguration: string,
): number => {
    const currentScrapingConfig = scrapingConfigurations?.find(
        (scrapingConf) => scrapingConf.id === recordScrapingConfiguration,
    );
    return currentScrapingConfig?.geoLocationCountry;
};

export const getTableDataWithKeys = (tableData: IKeywordsReportTableData): IKeywordsReportTableDataWithKeys => {
    return {
        ...tableData,
        records: tableData.records.map((r) => ({
            key: `${r.keyword}-${r.scrapingConfigurationId}`, // Unique key, required row selection and expanding
            ...r,
        })),
    };
};

export const transformDataToRow = (props: ITransferDataToRowProps): Array<IKeywordsReportRecordManagedData> => {
    const {
        data,
        tableTab,
        tableGranularity,
        selectedSites,
        scrapingConfigurations,
        languagesList,
        duration,
        campaign,
        device,
        isDemoCampaign,
        keywordToTargetUrls,
    } = props;
    let transformedData = [];
    try {
        transformedData = data.records?.map((item: IKeywordsReportRecordsRawData) => {
            const trends = getPositionsForKeyword(item, tableTab, tableGranularity, selectedSites);
            const SerpFeatures: string[] = item.serpFeatures?.keywordSerpFeatures;
            const SiteSerpFeatures = item.serpFeatures?.siteSerpFeatures;
            const siteData = {
                amountOfAdditionalSites: item.urlCount - 1, //reduce the top url from count
                site: item.topUrl ? item.topUrl.replace("https://www.", "") : "",
                displayedSite: item.topUrl ? transformDisplayedURL(item.topUrl.replace("https://www.", "")) : undefined,
                tooltipAdditionalSites: translate("seo.keywordsCampaign.keywordsReport.keywordsTable.topURL.tooltip"),
            };
            const durationObject: DatesRange = getRTDurationObjectForCampaign(duration, campaign);
            return {
                ...item,
                ...trends,
                SerpFeatures,
                SiteSerpFeatures,
                SearchTerm: item.keyword, //required for serp features column cells' tooltip
                targetUrl: siteData,
                numberOfUrls: siteData,
                geoLocation: getLocationCellDetails(
                    item.geoLocation,
                    item.scrapingConfigurationId,
                    scrapingConfigurations,
                    languagesList,
                ),
                searchEngine: getSearchEngineByScrapingConfigId(scrapingConfigurations, item.scrapingConfigurationId),
                device, //used in dashboards
                serpLinkDate: durationObject.to.format(serverResponseFormat),
                lastUpdated: item.lastUpdated ?? null,
                lastUpdatedFormatted: item.lastUpdated
                    ? getFormattedLatUpdatedTime(item.lastUpdated)
                    : translate("app.analysis.common.na"),
                volume: item.volume ? item.volume : translate("app.analysis.common.na"),
                cpc: item.cpc ? `$${item.cpc.toFixed(2)}` : translate("app.analysis.common.na"),
                intent: {
                    PrimaryIntent: item.intent,
                },
                tags: item.tags?.map((tag) => ({
                    text: tag,
                })),
                duration, //required here for dashboard which does not contain this param as part of the url
                campaignGuid: campaign.id, //required here for dashboard which does not contain this param as part of the url
                isDemoCampaign,
                keywordToTargetUrls: keywordToTargetUrls?.[item.keyword] ?? [],

                aiTagging: {
                    isLoading: data?.aiTagging?.isLoading ?? false,
                },
            };
        });
    } catch (error) {
        Sentry.error(`Error parsing table data - Keywords report`, error, {
            extra: { cause: data.records[0] },
        });
    }
    return transformedData;
};

export const updateCampaign = async (
    campaignData: IKeywordsCampaignsUpdateCampaignData,
    campaignId: string,
): Promise<IKeywordsCampaignsCampaignRawData> => {
    const updateUrl = `${getSingleCampaignURL(false)}/${campaignId}/${UPDATE_SETTINGS_SUFFIX}`;
    return fetchService.put<IKeywordsCampaignsCampaignRawData>(
        updateUrl,
        normalizeCampaignSettingsPayload(campaignData),
    );
};

export type ISharedCampaign<T> = T & { isShared: boolean };
export const getCampaign = (
    campaignId: string,
    campaignsConfigurations: ISharedCampaign<IKeywordsCampaignData>[],
    isDemoCampaign: boolean,
    preventAutoCancellation?: boolean,
): Promise<IRTCurrentCampaignData> => {
    return queryClient.fetchQuery({
        queryKey: createRankTrackerQueryKey(RankTrackerQueryType.CurrentCampaign, [campaignId]),
        queryFn: async () => {
            const url = `${getSingleCampaignURL(isDemoCampaign)}/${campaignId}`;
            const campaign: IRTCurrentCampaignData = await fetchService.get(url, undefined, {
                preventAutoCancellation,
            });

            const listCampaign = campaignsConfigurations?.find(
                (campaignConfiguration) => campaignConfiguration.id === campaignId,
            );
            const isEditable = campaign.assetRolesSettings?.userAssetRoles.some((user) => {
                return user.userId === swSettings.user.id;
            });

            return {
                ...withCampaignDatesUtc(campaign),
                isShared: listCampaign?.isShared,
                lastMigrationDate: campaign.lastMigrationDate ? dayjs(campaign.lastMigrationDate) : null,
                isEditable,
            };
        },
        staleTime: 0, // re-fetch every time to not introduce breaking changes
        gcTime: Infinity, // keep it always accessible during the session (e.g., in CustomUrlBuilderService.ts).
    });
};

export const updateKeywordsFromCampaign = async (
    campaignData: IRTRemoveKeywordsOrUpdateTagsOnly,
    campaignId: string,
): Promise<IRTCurrentCampaignData> => {
    const updateUrl = `${getSingleCampaignURL(false)}/${campaignId}/${UPDATE_KEYWORDS_SUFFIX}`;
    return fetchService.put(updateUrl, normalizeKeywordUpdatePayload(campaignData));
};

const getSelectedSitesData = (
    chosenSites: string[],
    currentCampaignMainSite: IRTCurrentCampaignData,
): ISelectedSites[] => {
    const managedSites = chosenSites
        .filter((visibleSite) => visibleSite)
        .map((site, index) => {
            const competitor =
                site === currentCampaignMainSite?.site
                    ? currentCampaignMainSite
                    : currentCampaignMainSite?.competitors.find((competitor) => competitor.site === site);
            return {
                id: site,
                color: competitor?.color,
                data: {
                    type: "website" as StateObjectType,
                    mainItem: competitor?.asset,
                },
                isPrimary: index === 0,
                updatedTime: dayjs().utc().toString(),
                isFavorite: false,
            };
        });
    return managedSites;
};
export const getDefaultSiteNoSelectionMade = (
    currentCampaignMainSite: IRTCurrentCampaignData,
    sites: string,
): ISelectedSites[] => {
    if (!currentCampaignMainSite) {
        return [];
    }
    return sites.split(",").map((item) => {
        const isMainSite = item === currentCampaignMainSite?.site;
        const id = isMainSite
            ? currentCampaignMainSite?.site
            : currentCampaignMainSite?.competitors.find((competitor) => competitor.site === item)?.site;
        const asset = isMainSite
            ? currentCampaignMainSite?.asset
            : currentCampaignMainSite?.competitors.find((competitor) => competitor.site === item)?.asset;
        const color = isMainSite
            ? currentCampaignMainSite.color
            : currentCampaignMainSite?.competitors.find((competitor) => competitor.site === item)?.color;
        return {
            id,
            data: {
                type: "website",
                mainItem: asset,
            },
            isPrimary: isMainSite,
            updatedTime: dayjs().utc().toString(),
            isFavorite: false,
            color,
        };
    });
};

export const formatSitesFromURL = (
    sites: string,
    currentCampaignMainSite: IRTCurrentCampaignData,
): ISelectedSites[] => {
    const chosenSites = sites?.split(",");
    return chosenSites
        ? getSelectedSitesData(chosenSites, currentCampaignMainSite)
        : getDefaultSiteNoSelectionMade(currentCampaignMainSite, sites);
};

export const getAllSitesFromCampaign = (campingData: IRTCurrentCampaignData): string => {
    if (!campingData) {
        return "";
    }
    const competitors = campingData.competitors ?? [];
    return [campingData.site, ...competitors.map((competitor) => competitor.site)].join(",");
};

export const getRemoveKeywordsObjectForRequest = (
    selectedRows: IKeywordsReportRecordManagedData[],
    currentCampaignConfiguration: IRTCurrentCampaignData,
): IRTRemoveKeywordsOrUpdateTagsOnly => {
    const changedDataMap: Record<string, IRTUpdateCampaignRemovedKeywords> =
        currentCampaignConfiguration.scrapingConfigurations.reduce((acc, scrapingConfiguration) => {
            acc[scrapingConfiguration.id] = {
                scrapingGuid: scrapingConfiguration.id,
                removedKeywords: [],
            };
            return acc;
        }, {});

    selectedRows.forEach((selected) => {
        const matchingScrapingConfig = changedDataMap[selected.scrapingConfigurationId];
        matchingScrapingConfig.removedKeywords.push(selected.keyword);
    });

    const removeKeywordsData: IRTRemoveKeywordsOrUpdateTagsOnly = {
        keywordChanges: Object.values(changedDataMap).filter((config) => config.removedKeywords.length > 0),
    };
    return removeKeywordsData;
};
